import React, { useMemo } from 'react'

type MetricSparklineProps = {
    values: number[]
    className?: string
    stroke?: string
}

/** Lightweight SVG sparkline; needs at least 2 points for a line. */
export function MetricSparkline({
    values,
    className = '',
    stroke = '#34d399',
}: MetricSparklineProps) {
    const { pathD, hasLine } = useMemo(() => {
        const pts = values.filter((v) => typeof v === 'number' && !Number.isNaN(v))
        if (pts.length < 2) return { pathD: '', hasLine: false }
        const w = 120
        const h = 36
        const pad = 2
        let min = Math.min(...pts)
        let max = Math.max(...pts)
        if (min === max) {
            min -= 1
            max += 1
        }
        const xStep = (w - pad * 2) / Math.max(pts.length - 1, 1)
        const coords = pts.map((v, i) => {
            const x = pad + i * xStep
            const t = (v - min) / (max - min)
            const y = h - pad - t * (h - pad * 2)
            return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`
        })
        return { pathD: coords.join(' '), hasLine: true }
    }, [values])

    if (!hasLine) {
        return (
            <div
                className={`flex h-9 w-[120px] items-center justify-center rounded border border-dashed border-zinc-700 text-[10px] text-zinc-600 ${className}`}
            >
                Collecting…
            </div>
        )
    }

    return (
        <svg
            className={className}
            width={120}
            height={36}
            viewBox="0 0 120 36"
            aria-hidden
        >
            <path d={pathD} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}
